import React, { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../utils/apiService";
import { auth } from "../firebase/config";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        const storedUser = authService.getUser();
        const storedTenant = authService.getTenant();

        if (token && storedUser) {
          setCurrentUser(storedUser);
          setCurrentTenant(storedTenant);
          setLoading(false);

          setTimeout(async () => {
            try {
              const userData = await authService
                .getCurrentUser()
                .catch((err) => {
                  console.log(
                    "Silent auth refresh failed, using stored credentials",
                  );
                  return { user: storedUser, tenant: storedTenant };
                });

              if (userData && userData.user) {
                setCurrentUser(userData.user);
                setCurrentTenant(userData.tenant);
              }
            } catch (error) {
              console.error("Background refresh error:", error);
            }
          }, 1000);

          return;
        } else {
          setCurrentUser(null);
          setCurrentTenant(null);
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        const storedUser = authService.getUser();
        const storedTenant = authService.getTenant();
        if (storedUser && localStorage.getItem("token")) {
          setCurrentUser(storedUser);
          setCurrentTenant(storedTenant);
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      setCurrentUser(response.user);
      setCurrentTenant(response.tenant);
      navigate("/dashboard");
      return response;
    } catch (error) {
      console.error("Login error in AuthContext:", error);
      throw error;
    }
  };

  const googleLogin = async (phoneNumber = null) => {
    try {
      await auth.signOut();

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });

      const result = await signInWithPopup(auth, provider);

      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;
      const idToken = await result.user.getIdToken();

      const response = await authService.firebaseLogin({
        idToken,
        accessToken,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        phoneNumber: phoneNumber,
      });

      setCurrentUser(response.user);
      setCurrentTenant(response.tenant);

      return response;
    } catch (error) {
      console.error("Google login error in AuthContext:", error);

      if (error.needsPhoneNumber) {
        throw error;
      }

      await auth
        .signOut()
        .catch((e) =>
          console.error("Error signing out after failed login:", e),
        );

      if (error.code) {
        throw {
          code: error.code,
          message: error.message || "Google authentication failed",
        };
      } else if (error.response) {
        throw {
          status: error.response.status,
          message:
            error.response.data?.message || "Server authentication failed",
          response: error.response,
          needsPhoneNumber: error.response.data?.needsPhoneNumber,
        };
      } else {
        throw error;
      }
    }
  };

  const registerTenant = async (tenantData, userData) => {
    const registrationData = {
      tenantName: tenantData.name,
      username: userData.username,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
      plan: tenantData.plan || "free",
    };

    const response = await authService.registerTenant(registrationData);
    setCurrentUser(response.user);
    setCurrentTenant(response.tenant);
    return response;
  };

  // Register user function (admin only)
  const registerUser = async (userData) => {
    return await authService.registerUser(userData);
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setCurrentUser(null);
    setCurrentTenant(null);
    // After logout, redirect to login page
    navigate("/login", { replace: true });
  };

  // Update user function
  const updateUser = (userData) => {
    setCurrentUser(userData);
    localStorage.setItem("user", JSON.stringify(userData)); // Keeping fallback as 'user' although it should be AUTH_CONFIG.USER_KEY
  };

  // Update tenant function
  const updateTenant = (tenantData) => {
    setCurrentTenant(tenantData);
    localStorage.setItem("tenant", JSON.stringify(tenantData));
  };

  const value = {
    currentUser,
    currentTenant,
    login,
    googleLogin,
    registerTenant,
    registerUser,
    logout,
    updateUser,
    updateTenant,
    isAdmin: currentUser?.role === "admin",
    isAuthenticated: !!currentUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext